import { useMemo } from "react";
import { useQuery, type QueryStatus } from "@tanstack/react-query";
import { format, subDays, subMonths } from "date-fns";
import { ArrowUpRight, ArrowDownRight, Target, TrendingUp, IndianRupee, BarChart3, AlertTriangle } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { LockedCard } from "@/components/layout/locked-content";

import { cn } from "@/lib/utils";
import { PaidMediaApiFactory } from "@/apis/brand-os/paid-media";
import type { AdsIntelligenceOverview, WoWChange } from "@/schema/brand-os/paid-media";

type CardItem = {
  id: string;
  label: string;
  Icon: LucideIcon;
  iconClassName: string;
  value: string | null;
  wow: WoWChange | null;
  subtitle: string | null;
};

function formatCurrency(value: number | null | undefined, currency: string): string | null {
  if (value == null) return null;
  if (currency === "INR") return `₹${value.toLocaleString("en-IN")}`;
  return `${currency === "USD" ? "$" : currency}${value.toLocaleString("en-US")}`;
}

function formatSpend(value: number | null | undefined, currency: string): string | null {
  if (value == null) return null;
  const symbol = currency === "INR" ? "₹" : currency === "USD" ? "$" : currency;
  if (value >= 100000) return `${symbol}${(value / 100000).toFixed(1)}L`;
  if (value >= 1000) return `${symbol}${(value / 1000).toFixed(1)}K`;
  return `${symbol}${value.toLocaleString()}`;
}

function buildCards(data: AdsIntelligenceOverview | undefined): CardItem[] {
  return [
    {
      id: "cac",
      label: "Blended CAC",
      Icon: Target,
      iconClassName: "bg-emerald-50 text-emerald-600",
      value: data ? formatCurrency(data.metrics.cac, data.currency) : null,
      wow: data?.wow_changes.cac ?? null,
      subtitle: null,
    },
    {
      id: "roas",
      label: "Overall ROAS",
      Icon: TrendingUp,
      iconClassName: "bg-blue-50 text-blue-600",
      value: data?.metrics.roas != null ? `${data.metrics.roas.toFixed(1)}x` : null,
      wow: data?.wow_changes.roas ?? null,
      subtitle: null,
    },
    {
      id: "mtd_spend",
      label: "MTD Spend",
      Icon: IndianRupee,
      iconClassName: "bg-amber-50 text-amber-600",
      value: data ? formatSpend(data.budget.mtd_spend ?? data.metrics.total_spend, data.currency) : null,
      wow: data?.wow_changes.spend ?? null,
      subtitle: (() => {
        if (!data) return null;
        const { pacing_pct, total_budget } = data.budget;
        if (pacing_pct != null && total_budget != null) return `${Math.round(pacing_pct)}% of ${formatSpend(total_budget, data.currency)}`;
        return null;
      })(),
    },
    {
      id: "active_campaigns",
      label: "Active Campaigns",
      Icon: BarChart3,
      iconClassName: "bg-violet-50 text-violet-600",
      value: data?.metrics.active_campaigns != null ? String(data.metrics.active_campaigns) : null,
      wow: null,
      subtitle: data?.budget.overpacing_campaigns != null && data.budget.overpacing_campaigns > 0 ? `${data.budget.overpacing_campaigns} overpacing` : null,
    },
  ];
}

function WoWBadge({ wow }: { wow: WoWChange }) {
  const { change_pct, direction } = wow;
  const isPositive = direction === "up" || direction === "increasing" || (change_pct != null && change_pct > 0);
  const isNegative = direction === "down" || direction === "decreasing" || (change_pct != null && change_pct < 0);
  const label = change_pct != null ? `${change_pct > 0 ? "+" : ""}${change_pct.toFixed(1)}% WoW` : null;

  if (!label) return null;

  return (
    <span className="flex items-center gap-1">
      {isPositive && <ArrowUpRight className="size-3.5 text-success" strokeWidth={2.5} />}
      {isNegative && <ArrowDownRight className="size-3.5 text-destructive" strokeWidth={2.5} />}
      <span className={cn("text-xs font-medium", isPositive ? "text-success" : isNegative ? "text-destructive" : "text-muted-foreground")}>{label}</span>
    </span>
  );
}

function ScoreCard({ label, value, Icon, iconClassName, wow, subtitle }: CardItem) {
  if (value == null) return <LockedCard message="Not enough data yet" className="p-0" />;

  const isOverpacing = subtitle?.includes("overpacing");

  return (
    <Card className="py-5">
      <CardContent className="px-5 space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">{label}</p>
          <div className={cn("flex size-8 items-center justify-center rounded-lg", iconClassName)}>
            <Icon className="size-4" strokeWidth={2.5} />
          </div>
        </div>
        <p className="text-2xl font-bold tracking-tight">{value}</p>
        <div className="flex items-center gap-1.5">
          {wow && <WoWBadge wow={wow} />}
          {subtitle && (
            <span className="flex items-center gap-1">
              {isOverpacing && <AlertTriangle className="size-3.5 text-amber-500" strokeWidth={2.5} />}
              <span className={cn("text-xs font-medium", isOverpacing ? "text-amber-600" : "text-muted-foreground")}>{subtitle}</span>
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ScoreContentGrid({ cards }: { cards: CardItem[] }) {
  if (!cards.length) return <LockedCard message="Not enough data yet" className="p-0" />;
  return (
    <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
      {cards.map((card) => (
        <ScoreCard key={card.id} {...card} />
      ))}
    </div>
  );
}

function OverviewScoreCardContent({ status, cards }: { status: QueryStatus; cards: CardItem[] }) {
  switch (status) {
    case "pending":
      return <OverviewScoreCardSkeleton />;
    case "error":
      return <LockedCard message="Paid media data will appear once campaigns are active" />;
    case "success":
      return <ScoreContentGrid cards={cards} />;
  }
}

export function OverviewScoreCards() {
  const params = useMemo(() => {
    const yesterday = subDays(new Date(), 1);
    return {
      date_range_start: format(subMonths(yesterday, 1), "yyyy-MM-dd"),
      date_range_end: format(yesterday, "yyyy-MM-dd"),
    };
  }, []);

  const { data, status } = useQuery(PaidMediaApiFactory.Queries.FetchOverview(params));
  const cards = buildCards(data);

  return <OverviewScoreCardContent status={status} cards={cards} />;
}

function OverviewScoreCardSkeleton() {
  return (
    <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
      {Array.from({ length: 4 }, (_, i) => (
        <Card key={i} className="py-5">
          <CardContent className="px-5 space-y-3">
            <div className="flex items-center justify-between">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="size-8 rounded-lg" />
            </div>
            <Skeleton className="h-8 w-20" />
            <Skeleton className="h-4 w-28" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
